// useTaskMedia — a task's MEDIA (ART-368): files uploaded onto it, and pushing a finished piece into a release
// asset slot. Uploads run the ordinary media flow (`useUploadMedia`) first and then name the finished uploads on
// the task, so a task file is an ordinary band media asset and a push points the release at the same one.
// Server-confirm THEN repaint, like the other non-optimistic task writes; nothing reports success unpersisted.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandMutation } from "./useBandQuery.js";
import { useUploadMedia, mediaKindForFile } from "./useMedia.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type TaskDetail = components["schemas"]["TaskDetail"];
type TaskMediaDto = components["schemas"]["TaskMediaDto"];
type PushTaskMediaRequest = components["schemas"]["PushTaskMediaRequest"];
type PushTaskMediaResult = components["schemas"]["PushTaskMediaResult"];

export type { TaskMediaDto, PushTaskMediaRequest, PushTaskMediaResult };

/** Write the server's answer into the drawer's cache and refresh what shows tasks. */
function useSettle() {
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return (taskId: string, detail: TaskDetail | undefined) => {
    if (detail) qc.setQueryData(["band", bandId, "task", taskId], detail);
    void qc.invalidateQueries({ queryKey: ["band", bandId, "task", taskId] });
    void qc.invalidateQueries({ queryKey: ["band", bandId, "tasks"] });
  };
}

/** Upload files and attach them to the task. Rejects (and toasts) if any upload or the attach fails. */
export function useAttachTaskMedia() {
  const { api } = useApp();
  const { uploadMedia } = useUploadMedia();
  const settle = useSettle();

  return useBandMutation<TaskDetail, { taskId: string; files: File[] }>(
    async (band, { taskId, files }) => {
      const mediaIds: string[] = [];
      for (const file of files) {
        const media = await uploadMedia(file, { kind: mediaKindForFile(file), purpose: "task-media" });
        if (media.mediaId) mediaIds.push(media.mediaId);
      }
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tasks/{taskId}/media", {
        params: { path: { bandId: band, taskId } },
        body: { mediaIds },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (res, vars) => settle(vars.taskId, res),
      onError: (err) => pushToast(errorMessage(err, "Couldn't upload")),
    },
  );
}

/** Take a file off the task. The file stays wherever it was pushed. */
export function useRemoveTaskMedia() {
  const { api } = useApp();
  const settle = useSettle();

  return useBandMutation<TaskDetail, { taskId: string; taskMediaId: string }>(
    async (band, { taskId, taskMediaId }) => {
      const { data, error, response } = await api.DELETE("/api/v1/bands/{bandId}/tasks/{taskId}/media/{taskMediaId}", {
        params: { path: { bandId: band, taskId, taskMediaId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (res, vars) => settle(vars.taskId, res),
      onError: (err) => pushToast(errorMessage(err, "Couldn't remove it")),
    },
  );
}

/** Push one item into a release slot. The release's own reads refresh too, so its Assets tab shows the push. */
export function usePushTaskMedia() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  const settle = useSettle();

  return useBandMutation<PushTaskMediaResult, { taskId: string; body: PushTaskMediaRequest }>(
    async (band, { taskId, body }) => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/tasks/{taskId}/media/push", {
        params: { path: { bandId: band, taskId } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    {
      onSuccess: (res, vars) => {
        settle(vars.taskId, res.task);
        void qc.invalidateQueries({ queryKey: ["band", bandId, "release"] });
        void qc.invalidateQueries({ queryKey: ["band", bandId, "releases"] });
      },
      // The dialog shows the error inline, so no toast here.
    },
  );
}
